import BackButton from '@material-ui/icons/ArrowBack';
import BreadcrumbsContainer from 'features/Breadcrumbs/containers/BreadcrumbsContainer';
import Button from '@material-ui/core/Button';
import buildQuantityList from 'shared/utils/dropDownHelpers';
import cloneDeep from 'lodash.clonedeep';
import compose from 'recompose/compose';
import config from 'shared/config';
import ErrorBoundary from 'shared/components/ErrorBoundary';
import IconButton from '@material-ui/core/IconButton';
import IsEmpty from 'lodash.isempty';
import Paper from '@material-ui/core/Paper';
import ProductClass from 'models/Product';
import PropTypes from 'prop-types';
import React from 'react';
import Select from '@material-ui/core/Select';
import { DEPTH, HEIGHT, WIDTH } from 'models/constants';
import {
	Description,
	OnDisplay,
	ProductAttributes,
	ProductDimensions,
	ProductReviews,
	Rating,
	Sku,
	Softkit,
	StockQuantity,
	VendorName,
} from 'features/ProductDetails';
import { formatCurrency } from 'shared/utils/htmlHelpers';
import { getStoreName } from 'shared/utils/storeLocationHelpers';
import { SHOPPING_CART, WISH_LIST } from 'redux/domain/Cart/constants';
import { withStyles, withTheme } from '@material-ui/core/styles';
import ProductSlider from './ProductSlider';
import RelatedProducts from './RelatedProducts';
import styles from './styles.css';
import withRouter from '../../../shared/utils/WithRouter';

// This would normally just be called 'styles', but we already have an import with that name
const stylesMUI = theme => ({
	productPaperPadding: {
		padding: '6px 12px',
	},

	productSpacer: {
		padding: '4px 0',
	},

	primaryHeader: {
		margin: '0',
		/* Adjusting line-height so heading text is not smushed on wrap */
		lineHeight: '36px',
	},

	productReviews: {
		display: 'flex',
	},

	starRating: {
		marginRight: '0.5em',
	},

	productPrice: {
		fontSize: '2em',
		fontWeight: 'bold',
		padding: '0.5em 0',
	},

	productPriceReviewsVendorOff: {
		fontSize: '2em',
		fontWeight: 'normal',
		padding: '0.5em 0',
	},

	prodMsrp: {
		display: 'initial',
		textDecoration: 'line-through',
		fontSize: '0.5em',
		paddingLeft: '8px',
	},

	selectQuantity: {
		bottom: '0.3em',
		margin: '0.9em 0 1.1em 3px',
	},

	selectInput: {
		color: theme.palette.text.primary,
	},

	styleIconButton: {
		right: '6px',
		top: '5px',
		backgroundColor: 'transparent',
		color: 'inherit',
	},

	styleBackButton: {
		height: '35px',
		width: '35px',
	},
});

class ProductFull extends React.Component {
	constructor(props) {
		super();
		// on each load set the default product from the database
		// as the user configures the product the state will change forcing a re-render
		// but until needed this can stay out of Redux.
		this.state = {
			quantity: 1,
			configuredProduct: props.product,
		};

		this.handleComponentChange = this.handleComponentChange.bind(this);
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.product !== this.state.configuredProduct) {
			const nextPropsCopy = nextProps.product;
			nextPropsCopy.selectedComponents = nextProps.product.selectedComponents;
			this.setState({
				configuredProduct: nextPropsCopy,
			});
		}
	}
	/**
	 * Build an array of images if the image only has a single default image.
	 * @param {object} product
	 */
	getProductImages = product => {
		// Todo replace with lodash.
		if (product.pictureUrls === undefined) {
			return null;
		}

		// ProductSlider wants the pictureUrls in this format.
		return product.pictureUrls.length > 0
			? product.pictureUrls
			: [
					{
						full: product.pictureUrl,
						thumb: product.pictureUrl,
					},
				];
	};

	getProductAverageReview = productReviews => {
		let averageProductReviews = 0;
		let count = 0;
		productReviews.forEach(review => {
			averageProductReviews += review.Rating;
			count += 1;
		});
		return averageProductReviews / count;
	};

	/**
	 * User has selected a new kit component item.
	 * @param {productSKU} prevSelection previously selected component
	 * @param {productSKU} currentSelection updatedComponent
	 */
	handleComponentChange(prevSelection, currentSelection) {
		const product = cloneDeep(this.state.configuredProduct);
		product.removeSelectedComponent(prevSelection);
		product.addSelectedComponent(currentSelection);
		this.setState({
			configuredProduct: product,
		});
	}

	// Display a product as an overlay
	render() {
		const { theme } = this.props;
		const { addItemToCart, classes } = this.props;
		const { ShowCurrentPrice, ShowMsrp, ShowReviews, ShowVendor } = config.PRODUCT;
		const product = this.state.configuredProduct;
		const productReviews = product.productReviews.Items;
		if (!IsEmpty(productReviews)) {
			product.averageReview = this.getProductAverageReview(productReviews);
		}
		const categoryId = this.props.categoryId === 'search' ? '' : this.props.categoryId;
		product.parentCategoryId =
			categoryId === '' ? product.parentCategoryId : parseInt(categoryId, 10);
		// Display currentPrice as main price if ShowCurrentPrice is true AND currentPrice is not 0/null/undefined/NaN
		// Display MSRP as main price if currentPrice is not used AND ShowMsrp is true AND MSRP is not 0/null/undefined/NaN
		// In all other cases, display nothing.
		const mainPrice = (ShowCurrentPrice && product.currentPrice) || (ShowMsrp && product.msrp);
		const formatPrice = formatCurrency(mainPrice);
		// Display scratched out price if:
		// msrp exists (not undefined/null/NaN) and is not 0
		// msrp is greater than (and not equal to) mainPrice
		// ShowCurrentPrice and ShowMsrp are both true
		const formatSecondaryPrice =
			// eslint-disable-next-line no-nested-ternary
			ShowMsrp && product.msrp
				? product.msrp > mainPrice && formatCurrency(product.msrp)
				: ShowCurrentPrice && product.oldPrice
					? product.oldPrice > mainPrice && formatCurrency(product.oldPrice)
					: null;

		// get product dimensions
		const width = product.findSpecAttributes(WIDTH);
		const height = product.findSpecAttributes(HEIGHT);
		const depth = product.findSpecAttributes(DEPTH);

		// get selected member attributes (not including dimensions or empty attributes)

		const attributes = product.specAttributes.filter(
			attribute =>
				attribute.name !== 'Height' &&
				attribute.name !== 'Width' &&
				attribute.name !== 'Depth' &&
				attribute.value !== '',
		);

		return (
			<div className={styles['product-full-container']}>
				<div className={styles['product-navigation']}>
					<IconButton 
						onClick={() => this.props.router.navigate(`/category/${this.props.categoryId}`)} 
						className={classes.styleIconButton} >
							<BackButton	className={classes.styleBackButton} />
					</IconButton>
					<div className={styles['product-breadcrumbs']}>
						<BreadcrumbsContainer product={product} productCatId={categoryId} />
					</div>
				</div>
				<div className={styles['product-full-column1']}>
					<div className={styles['product-full-col-margins']}>
						<div className={styles['product-modal-slider']}>
							<ProductSlider pictureUrls={this.getProductImages(product)} key={product.productId} />
						</div>
					</div>
				</div>
				<div className={styles['product-full-column2']}>
					<div className={styles['product-full-col-margins']}>
						<Paper elevation={1} className={classes.productPaperPadding}>
							<ErrorBoundary>
								<div className={classes.productSpacer}>
									<h1 className={classes.primaryHeader}>{product.name}</h1>
								</div>
								{ShowVendor ? <VendorName vendorName={product.vendorName} /> : null}
								{ShowReviews ? (
									<div className={classes.productSpacer}>
										<div className={classes.productReviews}>
											<Rating productReviewRating={product.averageReview} />
											<div>
												{product.approvedTotalReviews}
												{product.approvedTotalReviews === 1 ? ' Review' : ' Reviews'}
											</div>
										</div>
									</div>
								) : null}
								{mainPrice ? (
									<div
										className={
											!ShowVendor && !ShowReviews
												? classes.productPriceReviewsVendorOff
												: classes.productPrice
										}
										style={
											product.oldPrice && product.oldPrice > product.currentPrice
												? { color: config.COLORS.ProductDetailSalePrice }
												: { color: theme.palette.text.productPrice }
										}
									>
										{formatPrice}
										{formatSecondaryPrice ? (
											<div
												className={classes.prodMsrp}
												style={{ color: theme.palette.text.secondary }}
											>
												{formatSecondaryPrice}
											</div>
										) : null}
									</div>
								) : null}
								{config.PRODUCT.ShowInStock ? (
									<StockQuantity productStockQuantity={product.stockQuantity} />
								) : null}
								{config.PRODUCT.ShowOnDisplay ? (
									<OnDisplay
										onDisplay={product.onDisplay}
										productStore={getStoreName(product.store)}
									/>
								) : null}
								{config.GENERAL.ShowCart ? (
									<div>
										<div className={classes.productSpacer}>
											<b>Quantity</b>
											<br />
											<Select
												value={this.state.quantity}
												onChange={event => this.setState({ quantity: event.target.value })}
												className={classes.selectQuantity}
												MenuProps={{
													transformOrigin: { vertical: 'top', horizontal: 'left' },
													getContentAnchorEl: null,
												}}
												inputProps={{ className: classes.selectInput }}
											>
												{buildQuantityList()}
											</Select>
										</div>
										<div className={classes.productSpacer}>
											<Button
												variant="contained"
												style={{
													backgroundColor: theme.palette.secondary.main,
													color: theme.palette.getContrastText(theme.palette.secondary.main),
												}}
												onClick={() =>
													addItemToCart(
														this.state.configuredProduct,
														SHOPPING_CART,
														this.state.quantity,
													)
												}
											>
												Add To Cart
											</Button>
										</div>
										<div className={classes.productSpacer}>
											<Button
												variant="contained"
												onClick={() =>
													addItemToCart(
														this.state.configuredProduct,
														WISH_LIST,
														this.state.quantity,
													)
												}
												color="inherit"
												style={{ backgroundColor: theme.palette.background.paper }}
											>
												Save To Idea Board
											</Button>
										</div>
									</div>
								) : null}
							</ErrorBoundary>
						</Paper>
						<Softkit product={product} handleSelection={this.handleComponentChange} />
						<Description shortDescription={product.shortDescription} />
						<Description fullDescription={product.fullDescription} />
						{ShowReviews && product.approvedTotalReviews > 0 && !IsEmpty(productReviews) ? (
							<ProductReviews product={product} productReviews={productReviews} />
						) : null}
						<ProductDimensions depth={depth} height={height} width={width} />
						<ErrorBoundary>
							{IsEmpty(product.addonProducts) ? null : (
								<div className={styles['addon-items-container']}>
									<RelatedProducts
										relatedProducts={product.addonProducts}
										title={config.ProductAddOnLabel}
									/>
								</div>
							)}
						</ErrorBoundary>
						<ErrorBoundary>
							{IsEmpty(product.relatedProducts) ? null : (
								<div className={styles['related-items-container']}>
									<RelatedProducts relatedProducts={product.relatedProducts} />
								</div>
							)}
						</ErrorBoundary>
						{!IsEmpty(attributes) ? (
							<ProductAttributes attributes={attributes} productId={product.productId} />
						) : null}
						<Sku
							isSoftkit={product.isSoftkit}
							selectedComponents={product.selectedComponents}
							sku={product.sku}
						/>
						{/* Spacer to make space between the last card and the bottom of the page */}
						<div className={styles['bottom-spacer']} />
					</div>
				</div>
			</div>
		);
	}
}

ProductFull.propTypes = {
	addItemToCart: PropTypes.func.isRequired,
	categoryId: PropTypes.string,
	classes: PropTypes.shape({
		productPaperPadding: PropTypes.string,
		productSpacer: PropTypes.string,
		primaryHeader: PropTypes.string,
		productReviews: PropTypes.string,
		productPrice: PropTypes.string,
		prodMsrp: PropTypes.string,
		selectInput: PropTypes.string,
		selectQuantity: PropTypes.string,
		starRating: PropTypes.string,
	}).isRequired,
	product: PropTypes.instanceOf(ProductClass).isRequired,
	theme: PropTypes.instanceOf(Object).isRequired,
};

ProductFull.defaultProps = {
	categoryId: '',
};

export default withRouter(compose(withStyles(stylesMUI, { name: 'ProductFull' }), withTheme)(ProductFull));
